/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
import React, {ReactNode, useEffect, useState} from 'react';
import s from './ArrowsNavigationWrapper.scss';
import {classes} from './ArrowsNavigationWrapper.st.css';
import {IconButton} from 'wix-ui-tpa/cssVars';
import {ChevronLeft, ChevronRight} from '@wix/wix-ui-icons-common/on-stage';
import {withGlobals} from '../../../../globalPropsContext';
import {withTranslations} from '@wix/wixstores-client-common-components/dist/es/src/outOfIframes/translations';
import {ISliderGlobalProps} from '../../../sliderGlobalStrategy';
import {useEnvironment, useExperiments} from '@wix/yoshi-flow-editor';
import classNames from 'classnames';
import {Experiments, keyboardEvents} from '../../../../constants';
import {useStylesParams} from '../../../../stylesParamsContext';
import {useStyles} from '@wix/tpa-settings/react';
import {ConditionalRender} from '../../../../category/components/ConditionalRender/ConditionalRender';

export type ArrowsNavigationWrapperProps = {
  navigateNext(): void;
  navigatePrev(): void;
  arrowsContainerHeight?: number;
  enoughProductsToNavigate: boolean;
  children: React.ReactNode;
} & ISliderGlobalProps;

export enum ArrowsNavigationWrapperDataHook {
  Root = 'ArrowsNavigationWrapperDataHook.Root',
  LeftArrow = 'ArrowsNavigationWrapperDataHook.LeftArrow',
  LeftArrowContainer = 'ArrowsNavigationWrapperDataHook.LeftArrowContainer',
  RightArrow = 'ArrowsNavigationWrapperDataHook.RightArrow',
  RightArrowContainer = 'ArrowsNavigationWrapperDataHook.RightArrowContainer',
}

type ArrowContainerProps = {
  className: string;
  isVisible: boolean;
  dataHook: string;
  children: ReactNode;
};
const ArrowContainer = ({className, isVisible, dataHook, children}: ArrowContainerProps) => {
  const {experiments} = useExperiments();
  const newSliderGalleryFeatures = experiments.enabled(Experiments.NewSliderGalleryFeaturesViewer);

  if (newSliderGalleryFeatures) {
    return (
      <ConditionalRender
        by={'gallery_showSliderNavigationArrows'}
        className={classNames(s.arrowContainer, className, {
          [s.newSliderGalleryFeatures]: newSliderGalleryFeatures,
          [s.visible]: isVisible,
        })}
        dataHook={dataHook}>
        {children}
      </ConditionalRender>
    );
  }

  return (
    <div className={classNames(s.arrowContainer, className, {[s.visible]: isVisible})} data-hook={dataHook}>
      {children}
    </div>
  );
};

export const ArrowsNavigationWrapper: React.FC<ArrowsNavigationWrapperProps> = withGlobals(
  withTranslations()(
    ({
      children,
      globals,
      navigateNext,
      navigatePrev,
      arrowsContainerHeight,
      enoughProductsToNavigate,
    }: // eslint-disable-next-line sonarjs/cognitive-complexity
    ArrowsNavigationWrapperProps) => {
      const {sliderGalleryPreviousProduct, sliderGalleryNextProduct} = globals.textsMap;
      const {isRTL, isMobile} = useEnvironment();
      const {experiments} = useExperiments();

      const leftArrowLabel = isRTL ? sliderGalleryNextProduct : sliderGalleryPreviousProduct;
      const rightArrowLabel = isRTL ? sliderGalleryPreviousProduct : sliderGalleryNextProduct;

      const leftArrowClick = () => (isRTL ? navigateNext() : navigatePrev());
      const rightArrowClick = () => (isRTL ? navigatePrev() : navigateNext());
      const newSliderGalleryFeatures = experiments.enabled(Experiments.NewSliderGalleryFeaturesViewer);
      const [leftArrowVisibility, setLeftArrowVisibility] = useState<boolean>(false);
      const [rightArrowVisibility, setRightArrowVisibility] = useState<boolean>(false);

      const stylesParams = useStylesParams();
      const styles = useStyles();

      const shouldAlwaysShowArrows =
        isMobile || !styles.get(stylesParams.gallery_sliderShowArrowsOnHoverOnly) || !newSliderGalleryFeatures;

      useEffect(() => {
        if (!arrowsContainerHeight) {
          return;
        }

        setBothArrowsVisibility(shouldAlwaysShowArrows);
      }, [shouldAlwaysShowArrows, arrowsContainerHeight]);

      /* istanbul ignore next: tested in sled */
      const inlineCssVars = {
        '--arrowsContainerHeight': arrowsContainerHeight ? `${arrowsContainerHeight}px` : 'unset',
      };

      const setBothArrowsVisibility = (visibility: boolean) => {
        setLeftArrowVisibility(visibility);
        setRightArrowVisibility(visibility);
      };

      const showOnlyLeftArrow = () => {
        setLeftArrowVisibility(true);
        setRightArrowVisibility(false);
      };

      const showOnlyRightArrow = () => {
        setLeftArrowVisibility(false);
        setRightArrowVisibility(true);
      };

      const onMouseOver = (event: React.MouseEvent<HTMLDivElement>) => {
        const bounds = event.currentTarget.getBoundingClientRect();
        const mouseX = event.clientX - bounds.left;
        if (mouseX > bounds.width / 2) {
          showOnlyRightArrow();
          return;
        }

        showOnlyLeftArrow();
      };

      const onMouseLeave = () => setBothArrowsVisibility(false);

      const onKeypressNavigation = (e: React.KeyboardEvent<HTMLDivElement>) => {
        if (e.keyCode === keyboardEvents.ARROW_RIGHT.keyCode) {
          rightArrowClick();
        }
        if (e.keyCode === keyboardEvents.ARROW_LEFT.keyCode) {
          leftArrowClick();
        }
      };

      return (
        <div
          data-hook={ArrowsNavigationWrapperDataHook.Root}
          className={s.root}
          style={inlineCssVars as React.CSSProperties}
          role={'group'}
          tabIndex={0}
          onKeyDown={onKeypressNavigation}
          {...(shouldAlwaysShowArrows ? {} : {onMouseOver, onMouseLeave})}>
          <ArrowContainer
            className={s.left}
            isVisible={enoughProductsToNavigate && leftArrowVisibility}
            dataHook={ArrowsNavigationWrapperDataHook.LeftArrowContainer}>
            <IconButton
              aria-label={leftArrowLabel}
              className={classNames(classes.root, s.button)}
              data-hook={ArrowsNavigationWrapperDataHook.LeftArrow}
              icon={<ChevronLeft className={s.svgIcon} />}
              onClick={leftArrowClick}
            />
          </ArrowContainer>

          <div className={s.sliderWrapper}>{children}</div>

          <ArrowContainer
            className={s.right}
            isVisible={enoughProductsToNavigate && rightArrowVisibility}
            dataHook={ArrowsNavigationWrapperDataHook.RightArrowContainer}>
            <IconButton
              aria-label={rightArrowLabel}
              className={classNames(classes.root, s.button)}
              data-hook={ArrowsNavigationWrapperDataHook.RightArrow}
              icon={<ChevronRight className={s.svgIcon} />}
              onClick={rightArrowClick}
            />
          </ArrowContainer>
        </div>
      );
    }
  )
);
